import React, { useEffect, useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, RotateCcw, ChevronRight, Trophy, Target, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import StreakBadge from '../components/StreakBadge';
import { useApp } from '../context/AppContext';
import { formatResponseTime } from '../utils/noteHelpers';

const Summary = memo(function Summary() {
  const navigate = useNavigate();
  const { saveSession, checkRangeExpansion, streak } = useApp();
  
  const [sessionData, setSessionData] = useState(null);
  const [streakResult, setStreakResult] = useState(null);
  const [newNotesUnlocked, setNewNotesUnlocked] = useState(null);
  
  useEffect(() => {
    const storedData = sessionStorage.getItem('sessionResults');
    if (!storedData) {
      navigate('/');
      return;
    }
    
    const data = JSON.parse(storedData);
    setSessionData(data);
    
    // Save session and get streak result
    const result = saveSession({
      type: data.mode,
      duration: data.mode === 'timed' ? 60 : null,
      cardCount: data.results.length,
      accuracy: data.metrics.accuracy,
      avgResponseTime: data.metrics.avgResponseTime,
      challengingNotes: data.metrics.challengingNotes,
      partial: data.partial || false
    });
    
    setStreakResult(result);
    
    // Check if range should expand
    const newNotes = checkRangeExpansion();
    if (newNotes && newNotes.length > 0) {
      setNewNotesUnlocked(newNotes);
    }
    
    // Clear stored data
    sessionStorage.removeItem('sessionResults');
  }, [navigate, saveSession, checkRangeExpansion]);
  
  if (!sessionData) {
    return null;
  }
  
  const { metrics, mode, partial } = sessionData;
  const isGoodAccuracy = metrics.accuracy >= 80;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="min-h-screen bg-cream-100 paper-texture"
      data-testid="summary-screen"
    >
      <div className="max-w-lg mx-auto px-6 py-8">
        {/* Header */}
        <header className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
              isGoodAccuracy ? 'bg-sage-500/20' : 'bg-wood-100'
            }`}
          >
            {isGoodAccuracy ? (
              <Trophy className="w-10 h-10 text-sage-500" strokeWidth={1.5} />
            ) : (
              <Target className="w-10 h-10 text-wood-700" strokeWidth={1.5} />
            )}
          </motion.div>
          
          <h1 className="font-serif text-3xl text-cream-900 mb-2">
            {partial ? 'Session Saved' : isGoodAccuracy ? 'Great Work!' : 'Keep Practicing'}
          </h1>
          <p className="font-sans text-wood-700">
            {mode === 'timed' ? '1-minute timed session' : `${metrics.totalQuestions} cards completed`}
          </p>
        </header>
        
        {/* Streak Update */}
        {streakResult && (
          <Card className="mb-6 bg-gradient-to-br from-cream-100 to-wood-100 border-wood-300">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  {streakResult.streakBroken ? (
                    <>
                      <p className="text-sm text-coral-500 font-sans font-medium mb-1">Streak broken</p>
                      <p className="text-xs text-wood-700 font-sans">Starting fresh today!</p>
                    </>
                  ) : streakResult.isNewStreak ? (
                    <p className="text-sm text-sage-500 font-sans font-medium">
                      Streak continued! 🎉
                    </p>
                  ) : (
                    <p className="text-sm text-wood-700 font-sans">
                      Today's practice logged
                    </p>
                  )}
                </div>
                <StreakBadge count={streak.currentCount} size="md" />
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Main Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="stat-card" data-testid="accuracy-stat">
            <div className="stat-value text-sage-500">{metrics.accuracy}%</div>
            <div className="stat-label">Accuracy</div>
          </div>
          
          <div className="stat-card" data-testid="response-time-stat">
            <div className="stat-value">{formatResponseTime(metrics.avgResponseTime)}</div>
            <div className="stat-label">Avg Response</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-value text-sage-500">{metrics.correctCount}</div>
            <div className="stat-label">Correct</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-value text-coral-500">{metrics.incorrectCount}</div>
            <div className="stat-label">Incorrect</div>
          </div>
        </div>
        
        {/* Range Expansion Notification */}
        {newNotesUnlocked && newNotesUnlocked.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="mb-6 bg-sage-500/10 border-sage-500/30">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-sage-500/20 flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-5 h-5 text-sage-500" />
                  </div>
                  <div>
                    <p className="font-serif text-lg text-cream-900 mb-1">Range Expanded!</p>
                    <p className="font-sans text-sm text-wood-700">
                      New notes unlocked: <span className="font-medium">{newNotesUnlocked.join(', ')}</span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
        
        {/* Missed Notes in This Session */}
        {metrics.missedNotes && metrics.missedNotes.length > 0 && (
          <div className="mb-6">
            <h2 className="font-serif text-lg text-cream-900 mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-coral-500" strokeWidth={1.5} />
              Missed in This Session
            </h2>
            <Card className="border-wood-300 bg-cream-50">
              <CardContent className="divide-y divide-wood-200">
                {metrics.missedNotes.map(({ note, correct, incorrect, accuracy, avgTime }) => (
                  <div key={note} className="flex items-center justify-between py-3 first:pt-3 last:pb-3">
                    <div className="flex items-center gap-3">
                      <span className="font-serif text-xl text-cream-900 w-10">{note}</span>
                      <span className="text-xs text-coral-500 font-sans">
                        {incorrect} miss{incorrect > 1 ? 'es' : ''}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="font-sans text-sm text-wood-700">
                        {formatResponseTime(avgTime)} avg
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Challenging Notes (historically) */}
        {metrics.challengingNotes && metrics.challengingNotes.length > 0 && (
          <div className="mb-8">
            <h2 className="font-serif text-lg text-cream-900 mb-3 flex items-center gap-2">
              <Target className="w-5 h-5 text-wood-700" strokeWidth={1.5} />
              Focus Areas
            </h2>
            <div className="space-y-2">
              {metrics.challengingNotes.map(note => (
                <div key={note} className="challenging-note" data-testid={`challenging-note-${note}`}>
                  <span className="note-name">{note}</span>
                  <span className="text-xs uppercase tracking-wider text-wood-500">Needs work</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={() => navigate(`/practice?mode=${mode}`)}
            className="w-full bg-coral-500 hover:bg-coral-600 active:bg-coral-700 text-white font-serif py-6 rounded-btn"
            data-testid="practice-again-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Practice Again
          </Button>
          
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="w-full border-wood-300 text-wood-900 hover:bg-wood-100 font-sans py-6 rounded-btn"
            data-testid="go-home-btn"
          >
            <Home className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          
          <Button
            variant="ghost"
            onClick={() => navigate('/progress')}
            className="w-full text-wood-700 hover:text-cream-900 hover:bg-wood-100 font-sans"
            data-testid="view-progress-btn"
          >
            View All Progress
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
});

export default Summary;
